import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi, API } from "../../hooks/useApi";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock, ExternalLink, Upload } from "lucide-react";

const BOOL_FIELDS = ["publisher_source_approved", "source_available", "reader_imported", "reader_qa_passed", "download_approved", "director_approved"];
const EDITABLE_BOOL_FIELDS = ["publisher_source_approved", "reader_imported", "reader_qa_passed", "download_approved", "director_approved"];

function Flag({ label, value }) {
  return (
    <span data-testid={`manuscript-flag-${label}`} className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-wide border px-2 py-1 ${value ? "border-foreground text-foreground" : "border-foreground/25 text-foreground/50"}`}>
      {value ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
      {label.replace(/_/g, " ")}
    </span>
  );
}

function TitleCard({ t, missing, onSetField, onUpload }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const upload = async () => {
    if (!file) return toast.error("Choose a PDF file first.");
    setUploading(true);
    try {
      await onUpload(t.title_id, file);
      toast.success(`${t.title} source uploaded`);
      setFile(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="border border-border p-5 mb-4" data-testid={`manuscript-card-${t.title_id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div>
          <p className="text-sm font-medium">{t.title}</p>
          <p className="text-xs text-foreground/60">{t.book_id ? "Has catalog listing" : "Bundle-component only — no standalone listing"} · {t.source_pages || 0}pp · sku_edition_match: {t.sku_edition_match}</p>
        </div>
        {t.source_available && (
          <a href={`${API}/admin/manuscripts/${t.title_id}/preview`} target="_blank" rel="noreferrer" data-testid={`manuscript-preview-link-${t.title_id}`} className="text-xs uppercase tracking-wide underline flex items-center gap-1 hover:text-brandAccent">
            <ExternalLink className="w-3 h-3" /> Preview PDF
          </a>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {BOOL_FIELDS.map((f) => <Flag key={f} label={f} value={t[f]} />)}
      </div>
      <p className={`text-xs mb-3 ${t.ready_for_live_sale ? "text-foreground" : "text-foreground/60"}`} data-testid={`manuscript-ready-${t.title_id}`}>
        {t.ready_for_live_sale ? "READY FOR LIVE SALE" : missing ? "RAW PDF MISSING" : t.source_available ? "DIRECTOR APPROVAL PENDING" : "NOT YET STAGED"}
      </p>
      {t.status_note && <p className="text-xs text-foreground/70 mb-3">{t.status_note}</p>}

      {missing ? (
        <div className="flex items-center gap-2">
          <input type="file" accept="application/pdf" data-testid={`manuscript-upload-input-${t.title_id}`}
            onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-xs" />
          <button data-testid={`manuscript-upload-button-${t.title_id}`} onClick={upload} disabled={uploading}
            className="bg-foreground text-background px-3 py-1.5 text-xs uppercase tracking-wide disabled:opacity-40 flex items-center gap-1">
            <Upload className="w-3 h-3" /> {uploading ? "Uploading…" : "Upload original PDF"}
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2 items-center">
          {EDITABLE_BOOL_FIELDS.map((f) => {
            const prereqsMet = f !== "director_approved" || (
              t.publisher_source_approved && t.source_available && t.reader_imported && t.reader_qa_passed && t.sku_edition_match !== "conflict"
            );
            const disabled = !t[f] && !prereqsMet;
            return (
              <button
                key={f}
                data-testid={`manuscript-toggle-${f}-${t.title_id}`}
                onClick={() => onSetField(t.title_id, f, !t[f])}
                disabled={disabled}
                title={disabled ? "Prerequisites not met yet (publisher-approved, staged, imported, QA-passed, no SKU conflict)" : undefined}
                className="text-[11px] uppercase tracking-wide border border-border px-2 py-1 hover:border-foreground disabled:opacity-30 disabled:cursor-not-allowed"
              >
                {t[f] ? "Unset" : "Set"} {f.replace(/_/g, " ")}
              </button>
            );
          })}
          <select
            data-testid={`manuscript-sku-select-${t.title_id}`}
            value={t.sku_edition_match}
            onChange={(e) => onSetField(t.title_id, "sku_edition_match", null, e.target.value)}
            className="bg-background border border-border text-xs px-2 py-1"
          >
            {["match", "conflict", "unknown", "n/a"].map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}

function BundleCard({ b }) {
  return (
    <div className="border border-border p-5 mb-4" data-testid={`manuscript-bundle-${b.bundle_id}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium">{b.label}</p>
        <span data-testid={`manuscript-bundle-ready-${b.bundle_id}`} className={`text-[11px] uppercase tracking-wide border px-2 py-1 flex items-center gap-1 ${b.ready_for_live_sale ? "border-foreground text-foreground" : "border-foreground/25 text-foreground/50"}`}>
          {b.ready_for_live_sale ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
          {b.ready_for_live_sale ? "Ready for live sale" : "Live-sale blocked"}
        </span>
      </div>
      <p className="text-xs text-foreground/60">
        {b.components.map((c) => `${c?.title || "?"} (${c?.ready_for_live_sale ? "qualified" : "pending"})`).join(" · ")}
      </p>
    </div>
  );
}

export default function AdminManuscripts() {
  const api = useApi();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-manuscripts"], queryFn: async () => (await api.get("/admin/manuscripts")).data });

  const setField = async (titleId, field, value, textValue) => {
    try {
      const params = new URLSearchParams({ field });
      if (value !== null && value !== undefined) params.set("value", value);
      if (textValue !== undefined) params.set("text_value", textValue);
      await api.patch(`/admin/manuscripts/${titleId}/field?${params.toString()}`);
      qc.invalidateQueries({ queryKey: ["admin-manuscripts"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update this field.");
    }
  };

  const upload = async (titleId, file) => {
    const form = new FormData();
    form.append("file", file);
    await api.post(`/admin/manuscripts/${titleId}/upload`, form);
    qc.invalidateQueries({ queryKey: ["admin-manuscripts"] });
  };

  return (
    <div data-testid="admin-manuscripts-page">
      <h1 className="font-display text-display-xs mb-2">Manuscript Recovery &amp; Reader Qualification</h1>
      <p className="text-xs text-foreground/60 mb-8">Nine underlying titles, nine independently tracked statuses. Director approval is a deliberate, non-default action — nothing here is auto-approved.</p>

      <h2 className="text-sm uppercase tracking-widest text-foreground/70 mb-4 font-subtitle">Bundles</h2>
      {data?.bundles?.map((b) => <BundleCard key={b.bundle_id} b={b} />)}

      <h2 className="text-sm uppercase tracking-widest text-foreground/70 mb-4 mt-8 font-subtitle">Titles</h2>
      {data?.titles?.map((t) => (
        <TitleCard key={t.title_id} t={t} missing={data.missing_source_titles.includes(t.title_id)} onSetField={setField} onUpload={upload} />
      ))}
    </div>
  );
}
